import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  ShieldCheck, KeyRound, LockKeyhole, Search, RotateCcw, History,
  Smartphone, WifiOff, CheckCircle2, XCircle, Power,
} from "lucide-react";

const METHOD_LABEL = {
  password: "Password", pin: "Quick PIN", otp: "OTP",
  password_reset: "Password Reset", offline_password: "Offline Password", offline_pin: "Offline PIN",
};

function SettingRow({ label, desc, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b last:border-0">
      <div>
        <div className="text-sm font-bold">{label}</div>
        <div className="text-xs text-muted-foreground">{desc}</div>
      </div>
      <Switch data-testid={testId} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function UserSecurityCard({ found, refresh }) {
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const chip = (ok, yes, no) => (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${ok ? "bg-[#F3FFF5] text-[#0f6b3a]" : "bg-muted text-muted-foreground"}`}>
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {ok ? yes : no}
    </span>
  );

  return (
    <div className="mt-4 rounded-2xl border p-4 space-y-4" data-testid="security-user-card">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[180px]">
          <div className="font-bold">{found.name}</div>
          <div className="text-xs text-muted-foreground font-mono">+91 {found.mobile} · {found.role}</div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {chip(found.is_active, "Login Enabled", "Login Disabled")}
          {chip(found.has_password, "Password Set", "No Password")}
          {chip(found.has_pin, "PIN Set", "No PIN")}
          {found.must_change_password && (
            <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-800 px-2.5 py-1 text-[11px] font-bold">Must Change Password</span>
          )}
          {(found.locked_until || found.pin_locked_until) && (
            <span className="inline-flex items-center rounded-full bg-red-50 text-red-700 px-2.5 py-1 text-[11px] font-bold">Locked</span>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="flex gap-2">
          <Input
            data-testid="security-new-password"
            type="text"
            placeholder="New password (min 6 chars)"
            className="am-input flex-1"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
          />
          <Button
            data-testid="security-reset-password-btn"
            className="am-btn-primary shrink-0"
            disabled={busy || newPw.length < 6}
            onClick={() => act(async () => {
              await api.post(`/admin/users/${found.user_id}/reset-password`, { new_password: newPw });
              setNewPw("");
            }, "Password reset & account unlocked")}
          >
            <LockKeyhole className="w-4 h-4 mr-1.5" /> Reset Password
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="security-reset-pin-btn"
            variant="outline" className="rounded-full" disabled={busy}
            onClick={() => act(() => api.post(`/admin/users/${found.user_id}/reset-pin`), "Quick PIN removed")}
          >
            <KeyRound className="w-4 h-4 mr-1.5" /> Reset PIN
          </Button>
          <Button
            data-testid="security-force-change-btn"
            variant="outline" className="rounded-full" disabled={busy || found.must_change_password}
            onClick={() => act(() => api.post(`/admin/users/${found.user_id}/force-password-change`), "User must change password on next login")}
          >
            <RotateCcw className="w-4 h-4 mr-1.5" /> Force Password Change
          </Button>
          <Button
            data-testid="security-toggle-login-btn"
            variant="outline"
            className={`rounded-full ${found.is_active ? "text-destructive hover:text-destructive" : ""}`}
            disabled={busy}
            onClick={() => act(() => api.post(`/admin/users/${found.user_id}/toggle-login`), found.is_active ? "Login disabled" : "Login enabled")}
          >
            <Power className="w-4 h-4 mr-1.5" /> {found.is_active ? "Disable Login" : "Enable Login"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSecurity() {
  const { t } = useT();
  const [settings, setSettings] = useState(null);
  const [lookupMobile, setLookupMobile] = useState("");
  const [found, setFound] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [logFilter, setLogFilter] = useState("");

  useEffect(() => {
    api.get("/admin/auth-settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const loadLogs = useCallback((mobile = "") => {
    api.get("/admin/login-logs", { params: { mobile: mobile || undefined, limit: 50 } })
      .then((r) => { setLogs(r.data.logs); setLogTotal(r.data.total); })
      .catch(() => {});
  }, []);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  const toggleSetting = async (key, value) => {
    const prev = settings;
    setSettings({ ...settings, [key]: value });
    try {
      const r = await api.patch("/admin/auth-settings", { [key]: value });
      setSettings(r.data);
      toast.success("Login settings updated");
    } catch (e) {
      setSettings(prev);
      toast.error(e?.response?.data?.detail || "Failed to update");
    }
  };

  const findUser = async () => {
    if (!/^\d{6,15}$/.test(lookupMobile)) { toast.error("Enter a valid mobile number"); return; }
    try {
      const r = await api.get("/admin/users/find", { params: { mobile: lookupMobile } });
      setFound(r.data);
    } catch (e) {
      setFound(null);
      toast.error(e?.response?.data?.detail || "User not found");
    }
  };

  return (
    <DashboardShell title="Security & Login" subtitle="Login methods, account locks and login history">
      <div className="space-y-6 max-w-5xl" data-testid="admin-security-page">
        {/* Login method toggles */}
        <section className="am-card p-5">
          <h2 className="font-display text-lg font-extrabold flex items-center gap-2 mb-2">
            <ShieldCheck className="w-5 h-5 text-primary" /> Login Methods
          </h2>
          {!settings ? (
            <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
          ) : (
            <div>
              <SettingRow
                testId="setting-password-enabled"
                label="Password Login" desc="Default daily sign-in with mobile + password"
                checked={settings.password_enabled}
                onChange={(v) => toggleSetting("password_enabled", v)}
              />
              <SettingRow
                testId="setting-pin-enabled"
                label="Quick PIN Login" desc="4/6-digit device-bound PIN for fast field logins"
                checked={settings.pin_enabled}
                onChange={(v) => toggleSetting("pin_enabled", v)}
              />
              <SettingRow
                testId="setting-otp-enabled"
                label="OTP Daily Login" desc="When off, OTP still works for first login, forgot password and new devices"
                checked={settings.otp_enabled}
                onChange={(v) => toggleSetting("otp_enabled", v)}
              />
              <p className="text-xs text-muted-foreground mt-3">Super Admin accounts always keep password access so the platform can never be locked out.</p>
            </div>
          )}
        </section>

        {/* User security lookup */}
        <section className="am-card p-5">
          <h2 className="font-display text-lg font-extrabold flex items-center gap-2 mb-3">
            <Smartphone className="w-5 h-5 text-primary" /> Manage a User
          </h2>
          <div className="flex gap-2 max-w-md">
            <Input
              data-testid="security-lookup-mobile"
              placeholder="User mobile number"
              inputMode="numeric"
              className="am-input flex-1"
              value={lookupMobile}
              onChange={(e) => setLookupMobile(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => { if (e.key === "Enter") findUser(); }}
            />
            <Button data-testid="security-lookup-btn" className="am-btn-primary shrink-0" onClick={findUser}>
              <Search className="w-4 h-4 mr-1.5" /> Find
            </Button>
          </div>
          {found && <UserSecurityCard found={found} refresh={findUser} />}
        </section>

        {/* Login history */}
        <section className="am-card p-5">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <h2 className="font-display text-lg font-extrabold flex items-center gap-2 flex-1">
              <History className="w-5 h-5 text-primary" /> Login History
              <span className="text-xs font-semibold text-muted-foreground">({logTotal})</span>
            </h2>
            <div className="flex gap-2">
              <Input
                data-testid="security-logs-filter"
                placeholder="Filter by mobile"
                inputMode="numeric"
                className="am-input w-44"
                value={logFilter}
                onChange={(e) => setLogFilter(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => { if (e.key === "Enter") loadLogs(logFilter); }}
              />
              <Button data-testid="security-logs-refresh" variant="outline" className="rounded-full" onClick={() => loadLogs(logFilter)}>
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="security-logs-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                  <th className="py-2 pr-3">Time</th>
                  <th className="py-2 pr-3">Mobile</th>
                  <th className="py-2 pr-3">User</th>
                  <th className="py-2 pr-3">Method</th>
                  <th className="py-2 pr-3">Result</th>
                  <th className="py-2 pr-3">IP</th>
                  <th className="py-2">Device</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.log_id} className="border-b last:border-0" data-testid={`login-log-${l.log_id}`}>
                    <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs">{(l.created_at || "").replace("T", " ").slice(0, 19)}</td>
                    <td className="py-2 pr-3 font-mono">{l.mobile}</td>
                    <td className="py-2 pr-3">{l.name || "—"}<span className="text-xs text-muted-foreground"> {l.role ? `· ${l.role}` : ""}</span></td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold">
                        {l.offline && <WifiOff className="w-3 h-3 text-amber-600" />}
                        {METHOD_LABEL[l.method] || l.method}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      {l.success
                        ? <span className="text-[#0f6b3a] font-bold text-xs inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Success</span>
                        : <span className="text-red-600 font-bold text-xs inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> {l.reason || "Failed"}</span>}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{l.ip || "—"}</td>
                    <td className="py-2 font-mono text-xs truncate max-w-[120px]">{l.device_id ? l.device_id.slice(0, 12) : "—"}</td>
                  </tr>
                ))}
                {!logs.length && (
                  <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No login records yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </DashboardShell>
  );
}
